<script>
export default { name: 'ReassignModal' };
</script>

<script setup>
import Vue from 'vue';
import { ref, toRef } from 'vue';
import axios from 'axios';

const props = defineProps({
  userinfo: {
    type: Object,
    required: true,
  },
  displayid: {
    type: String,
    required: true,
  },
});

const emit = defineEmits(['close']);
const closeModal = () => emit('close');
const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;
const errorTimeout = 5000;

const originalSignerEmail = toRef(props.userinfo, 'email');
const newSignerEmail = ref('');
const newSignerName = ref('');
const isWorking = ref(false);

const error = ref('');
const hasPersonalizedMessage = ref(false);
const personalizedMessage = ref('');

// Validate name and email
const validateFields = () => {
  let noError = true;
  if (!newSignerName.value) {
    error.value = 'Signer name is required';
    noError = false;
  } else if (!newSignerEmail.value) {
    error.value = 'Email is required';
    noError = false;
  } else if (!emailRegex.test(newSignerEmail.value)) {
    error.value = 'Email is invalid';
    noError = false;
  }
  return noError;
};

// Request reassign to server
const handleReassign = async () => {
  if (!validateFields()) return;
  isWorking.value = true;
  const payload = {
    reassign_request: {
      original_signer_email: originalSignerEmail.value,
      new_signer_email: newSignerEmail.value,
      new_signer_name: newSignerName.value,
      display_id: props.displayid,
    },
  };

  if (hasPersonalizedMessage.value)
    payload.reassign_request.personalized_message = personalizedMessage.value;

  const result = await Vue.prototype.$harbourData.post('/reassign', payload);
  isWorking.value = false;
  if (result.data) confirmSuccess({ email: newSignerEmail.value, name: newSignerName.value });
  else {
    error.value = 'An error occured. Please try again.';
    resetError();
  }
};

// Hide errors after timeout seconds
const resetError = () => {
  setTimeout(() => {
    error.value = '';
  }, errorTimeout);
};

// Confirm success
const confirmSuccess = (newSigner) => {
  closeModal();
  emit('success', newSigner, originalSignerEmail.value);
};

// Reset errors when user inputs
const handleInput = () => {
  error.value = '';
};
</script>

<template>
  <div class="confirmation-modal-wrapper">
    <div class="modal-card">
      <header class="modal-card-head">
        <p class="modal-card-title is-size-6">Reassign to new signer</p>
        <button type="button" class="delete" @click="closeModal"></button>
      </header>

      <section class="modal-card-body">
        <b-field class="reassign-field" label="Name" horizontal>
          <b-input
            v-model="newSignerName"
            @input="handleInput"
            type="text"
            placeholder="New signer name..."></b-input>
        </b-field>

        <b-field class="reassign-field" label="Email" horizontal>
          <b-input
            v-model="newSignerEmail"
            @input="handleInput"
            type="email"
            placeholder="Email..."></b-input>
        </b-field>

        <div v-if="error">
          <b-message type="is-danger" class="is-size-7" :closable="false">
            {{ error }}
          </b-message>
        </div>

        <div @click="hasPersonalizedMessage = !hasPersonalizedMessage" class="is-size-7 mt-4">
          <span style="cursor: pointer"
            >Click to {{ hasPersonalizedMessage ? 'remove' : 'add' }} message (optional)</span
          >
        </div>

        <b-field
          class="reassign-field mt-2"
          label="Message"
          horizontal
          v-if="hasPersonalizedMessage">
          <b-input
            v-model="personalizedMessage"
            type="text"
            placeholder="Personalized message..."
            maxlength="100"></b-input>
        </b-field>
      </section>

      <footer class="modal-card-foot is-flex is-justify-content-space-between">
        <div></div>
        <b-button
          label="Reassign"
          class="is-size-6"
          type="is-primary"
          @click="handleReassign"
          :disabled="isWorking" />
      </footer>
    </div>
  </div>
</template>

<style scoped>
.modal-card-body {
  min-height: 175px;
  display: flex;
  flex-direction: column;
  justify-content: space-evenly;
}
.modal-card-foot {
  padding: 10px;
  margin: 0;
}
</style>
